<template>
    <button
        class="group relative flex items-center bg-transparent rounded-t-md shrink-0 px-6 | transition duration-200 | focus:outline-none"
        :class="textClassName"
        :disabled="isDisabled"
        type="button"
        @click="emit('click')"
        v-tooltip="tooltip"
    >
        <div
            class="flex-1 flex flex-col pointer-events-none rounded group-focus-visible:ring-2 group-focus-visible:ring-offset-2 group-focus-visible:ring-blue-500"
            :class="textAlignmentClassName"
            data-testid="activix-tab-label-container"
        >
            <div class="truncate" v-text="label" />
            <div class="text-xs text-gray-500 truncate" v-text="subLabel" v-if="subLabel" />
        </div>
        <div class="flex items-center h-full absolute top-0 right-0 mr-2" v-if="isActive">
            <slot name="moreButtons" />
        </div>
        <div
            class="absolute right-0 h-5 w-px -mr-px bg-gray-350 | transition duration-200"
            :class="{ 'group-hover:bg-transparent': !isDisabled }"
            data-testid="activix-tab-separator"
            v-if="!isActive && !hideSeparator"
        />
    </button>
</template>

<script lang="ts" setup>
    import { computed } from 'vue';
    import type { TabAlignment } from '@/components/activix-ui/ActivixTabs/types.ts';

    const props = withDefaults(
        defineProps<{
            label: string;
            subLabel?: string;
            isActive?: boolean;
            isDisabled?: boolean;
            tooltip?: string;
            hideSeparator?: boolean;
            textAlignment?: TabAlignment;
        }>(),
        {
            subLabel: '',
            isActive: false,
            isDisabled: false,
            tooltip: '',
            hideSeparator: true,
            textAlignment: 'center',
        }
    );

    const emit = defineEmits<(event: 'click') => void>();

    const textClassName = computed(() => {
        if (props.isDisabled) {
            return 'cursor-not-allowed text-gray-500';
        }

        if (props.isActive) {
            return 'text-gray-700 font-semibold';
        }

        return 'text-gray-600 hover:bg-gray-300 hover:text-gray-700';
    });

    const textAlignmentClassName = computed(() => {
        return {
            left: 'text-left',
            center: 'text-center',
        }[props.textAlignment];
    });
</script>
